import React from "react";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import { Link } from "react-router-dom";
import { useGetCmsQuery } from "../../services/auth";
import { PageHero, LoadingState, ErrorState } from "./PublicShared";
import "./PublicPages.scss";

export default function SecurityPolicy() {
  const { data: cmsData, isLoading, isError, refetch } = useGetCmsQuery();

  // If backend CMS ever adds a 'security_policy' field, use it directly
  const backendSecurityPolicy = (cmsData?.data as any)?.security_policy?.trim();
  const contactEmail = cmsData?.data?.contact_email || "support@itsmywall.com";
  const updatedAt = cmsData?.data?.updated_at;

  return (
    <div className="public_page_container">
      <PageHero
        badge="Trust & Security"
        badgeIcon={<ShieldOutlinedIcon />}
        title="Security Policy"
        subtitle="Our dedication to securing your designs, user credentials, and platform infrastructure."
        updatedAt={updatedAt}
      />

      <div className="public_card_container">
        {isLoading ? (
          <LoadingState lines={8} />
        ) : isError ? (
          <ErrorState
            title="Unable to load Security Policy"
            message="We were unable to reach our servers. Please check your connection and try again."
            onRetry={refetch}
          />
        ) : backendSecurityPolicy ? (
          <div
            className="public_doc_body"
            dangerouslySetInnerHTML={{ __html: backendSecurityPolicy }}
          />
        ) : (
          <div className="public_doc_body">
            <p>
              At <strong>It's My Wall</strong>, security is built into every layer of our platform. From the moment you sign in to when you synthesize, edit, and export AI-generated wall designs, we maintain rigorous technical and organizational controls to protect your data.
            </p>

            <h2>1. Data Encryption & Transmission</h2>
            <p>
              We protect your data both in transit and at rest using modern, industry-standard cryptographic protocols:
            </p>
            <ul>
              <li>
                <strong>In Transit:</strong> All communication between your browser and our application servers is encrypted using <code>TLS 1.3</code> (Transport Layer Security) with modern cipher suites and Strict Transport Security (HSTS).
              </li>
              <li>
                <strong>At Rest:</strong> All user database records, design parameters, and uploaded media files are encrypted using <code>AES-256</code> encryption.
              </li>
              <li>
                <strong>Asset Storage:</strong> Generated artwork, exported design packages, and original image assets are protected within secured cloud storage buckets with signed, time-limited URLs.
              </li>
            </ul>

            <h2>2. Authentication & Access Management</h2>
            <p>
              Your account security is critical to protecting your creative work:
            </p>
            <ul>
              <li>
                <strong>Password Protection:</strong> Passwords are never stored in plaintext. We utilize one-way cryptographic hashing algorithms with random salt to prevent reverse lookup.
              </li>
              <li>
                <strong>One-Time Password (OTP) Verification:</strong> Identity verification via time-based OTP is enforced during registration, password resets, and high-privilege account updates.
              </li>
              <li>
                <strong>Session Security:</strong> API requests utilize scoped, stateless Bearer tokens with strict expiration and revoking mechanisms on logout.
              </li>
            </ul>

            <h2>3. AI Generation & Intellectual Property Isolation</h2>
            <p>
              When utilizing our proprietary wall design engine:
            </p>
            <ul>
              <li>
                Your custom uploaded assets and customized regions are strictly isolated to your user account and never shared with other tenants.
              </li>
              <li>
                Saved designs and exported formats (PNG, SVG, PDF) remain your personal digital property as governed by our Terms & Conditions.
              </li>
            </ul>

            <h2>4. Infrastructure & Platform Resilience</h2>
            <p>
              Our application infrastructure is hosted within secure cloud facilities that adhere to industry certifications (ISO 27001, SOC 2 Type II):
            </p>
            <ul>
              <li>
                <strong>DDoS Mitigation:</strong> Automated cloud firewalls and rate-limiting protect our application against volumetric and application-layer attacks.
              </li>
              <li>
                <strong>System Monitoring:</strong> Continuous telemetry, audit logging, and automated alerts for anomalous activity 24/7/365.
              </li>
              <li>
                <strong>Backups:</strong> Redundant database snapshots taken regularly with point-in-time recovery capabilities.
              </li>
            </ul>

            <h2>5. Responsible Vulnerability Disclosure</h2>
            <p>
              We welcome security researchers and community members who want to help make It's My Wall safer for everyone. If you discover a security vulnerability or bug:
            </p>
            <blockquote>
              Please report it immediately to our security response team at{" "}
              <a href={`mailto:${contactEmail}?subject=Security%20Vulnerability%20Report`}>
                {contactEmail}
              </a>
              . Please include reproduction steps and avoid accessing or disrupting other users' data.
            </blockquote>

            <div className="faq_support_cta" style={{ marginTop: 36 }}>
              <div className="cta_text">
                <h4>Have questions about our security practices?</h4>
                <p>
                  Contact our security and compliance team for enterprise security reviews or inquiries.
                </p>
              </div>
              <Link to="/public/support" className="cta_btn">
                Contact Security Team
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
